// src/pages/user/QuestionWizard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import RatingButtons from "./RatingButton";
import ProgressDot from "./ProgressDot";
import { AuthApisUser } from "../api/userapi/userauth";
import "../assets/css/questionpages.css";

const QuestionWizard = () => {
  const [steps, setSteps] = useState([]); // all principles with sections/questions
  const [stepIndex, setStepIndex] = useState(0); // which principle we're on

  const navigate = useNavigate();
  const location = useLocation();

  // existing
  const [answers, setAnswers] = useState(() => location.state?.answers || {});

  // new — add targets state
  // const [targets, setTargets] = useState(() => location.state?.targets || {});
  // let tAns = localStorage.setItem("targets", JSON.stringify(targets));
  // let tans1 = localStorage.getItem("targets");
  // setTargets(JSON.parse(tans1));

  const [targets, setTargets] = useState(() => {
    return (
      location.state?.targets ||
      JSON.parse(sessionStorage.getItem("targets")) ||
      {}
    );
  });

  useEffect(() => {
    sessionStorage.setItem("targets", JSON.stringify(targets));
  }, [targets]);

  // --- Fetch data on mount ---
  // ✅ useEffect to fetch assessment data and handle "edit" mode
  useEffect(() => {
    const load = async () => {
      try {
        const res = await AuthApisUser.GetAssessment();
        const arr = res?.data?.data;
        if (!Array.isArray(arr)) {
          console.error("Expected an array but got:", arr);
          return;
        }

        // 1️⃣ Build your steps array (no change here)
        const built = arr.map((p) => ({
          principle: p.principle,
          sections: (p.category || []).flatMap((cat) =>
            (cat.metric || []).map((m) => ({
              title: m.metric,
              questions: (m.indicators || []).map((ind) => ({
                id: ind.id,
                que: ind.que,
                notes: ind.notes,
              })),
            }))
          ),
        }));

        const nonEmpty = built.filter(
          (st) => st.sections && st.sections.length > 0
        );

        setSteps(nonEmpty); // 2️⃣ Keep this as before

        // 3️⃣ NEW CODE: check if the page was opened in edit mode
        //    (location.state?.edit is passed from the Result page)
        if (location.state?.edit && nonEmpty.length > 0) {
          // Set the current step index to the last one
          setStepIndex(nonEmpty.length - 1);

          // Optional: remove the state flag so refreshing the page
          // doesn’t keep jumping to the last step
          window.history.replaceState({}, document.title);
        }
      } catch (err) {
        console.error("Failed to fetch assessment", err);
      }
    };

    load();

    // 4️⃣ Add `location` to dependencies so the edit flag is respected
  }, [location]);

  // --- Handlers ---
  const handleScoreSelect = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleTargetSelect = (id, value) => {
    setTargets((prev) => ({ ...prev, [id]: value }));
  };

  // const handleSubmit = async () => {
  //   // Build a set of all question ids present in either answers or targets
  //   const allIds = new Set([
  //     ...Object.keys(answers || {}),
  //     ...Object.keys(targets || {}),
  //   ]);

  //   // Create payload array in required shape
  //   const answersPayload = Array.from(allIds).map((idStr) => {
  //     const id = parseInt(idStr, 10);

  //     // Read score and target; default to 0 if missing (use null if backend prefers that)
  //    const score = answers[idStr] !== undefined ? answers[idStr] : 0;

  //     const target_score = targets[idStr] !== undefined ? targets[idStr] : 0;

  //     // const target_score = targets[idStr] !== undefined ? targets[idStr] : null;

  //     return {
  //       que_id: id,
  //       answer: score,
  //       target_score: target_score,
  //     };
  //   });

  //   const payload = { answers: answersPayload };

  //   try {
  //     console.log("Submitting payload:", payload); // debug - remove in prod
  //     const res = await AuthApisUser.SubmitAssessment(payload);
  //     console.log("Submit success:", res.data);

  //     // Pass response to Result page via state
  //     navigate("/user/result", {
  //       state: {
  //         resultData: res.data.data,
  //         rawAnswers: answers,
  //         rawTargets: targets,
  //       },
  //     });
  //   } catch (err) {
  //     console.error("Error submitting assessment:", err);
  //   }
  // };

  const handleSubmit = async () => {
    // ✅ Build ALL question IDs from the assessment itself
    const allIds = new Set();
    steps.forEach((step) => {
      step.sections.forEach((sec) => {
        sec.questions.forEach((q) => {
          allIds.add(q.id);
        });
      });
    });

    // ✅ Build payload
    const answersPayload = Array.from(allIds).map((id) => {
      const idStr = String(id);

      const score = answers[idStr] !== undefined ? answers[idStr] : 0;
      const target_score = targets[idStr] !== undefined ? targets[idStr] : 0;

      return {
        que_id: id,
        answer: score,
        target_score: target_score,
      };
    });

    const payload = { answers: answersPayload };

    console.log("Submitting payload:", payload);

    try {
      const res = await AuthApisUser.SubmitAssessment(payload);
      navigate("/user/result", {
        state: {
          resultData: res.data.data,
          rawAnswers: answers,
          rawTargets: targets,
        },
      });
    } catch (err) {
      console.error("Error submitting assessment:", err);
    }
  };

  const goNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/user/result"); // last step = submit
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/user/assessment-landing-page");
    }
  };

  if (!steps.length) return <p className="loading">Loading assessment...</p>;

  const currentStep = steps[stepIndex];

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="page">
        <button className="back-link" onClick={goBack}>
          &lt; back
        </button>

        <h1 className="title"> {currentStep.principle}</h1>

        <ProgressDot currentPage={stepIndex + 1} totalPages={steps.length} />

        {currentStep.sections.map((sec) => (
          <div key={sec.title} className="section1">
            <div className="section-header-row">
              <h2 className="section-title">{sec.title}</h2>

              <div className="section-labels">
                <span className="rating-label-score">Score</span>
                <span className="rating-label-target">Target</span>
              </div>
            </div>

            {sec.questions.map((q) => (
              <RatingButtons
                key={q.id}
                questionText={q.que}
                questionId={q.id}
                scoreValue={answers[q.id]}
                targetValue={targets[q.id]}
                onScoreSelect={handleScoreSelect}
                onTargetSelect={handleTargetSelect}
                notes={q.notes}
              />
            ))}
          </div>
        ))}

        <ProgressDot currentPage={stepIndex + 1} totalPages={steps.length} />

        <div className="navigation d-flex align-items-center">
          <button className="nav-btn" onClick={goBack}>
            Back
          </button>

          <button
            className="nav-btn primary"
            onClick={() =>
              stepIndex === steps.length - 1 ? handleSubmit() : goNext()
            }
          >
            {stepIndex === steps.length - 1 ? "Submit" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionWizard;
